import React, { useEffect, useMemo, useState } from 'react';
import { Box, Fade, IconButton } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { toast } from 'material-react-toastify';
import { useIsMobile } from '../hooks/useIsMobile';
import { SliderSlide } from '../../../interfaces/Slider/SliderSlide';
import { getSliderSlides } from '../../../api/Slider';
import { sharedColors } from '../../../utils/Style';

export function SliderCarousel() {
  const isMobile = useIsMobile();

  const [hovering, setHovering] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [slideIn, setSlideIn] = useState(true);
  const [allSlides, setAllSlides] = useState<SliderSlide[]>([]);
  const [imagesLoaded, setImagesLoaded] = useState(false);

  const slides = useMemo(
    () =>
      allSlides.filter(
        (s) =>
          s.type === 'all' ||
          (isMobile && s.type === 'mobile') ||
          (!isMobile && s.type === 'desktop'),
      ),
    [allSlides, isMobile],
  );

  useEffect(() => {
    setActiveIndex(0);
  }, [slides]);

  useEffect(() => {
    getSliderSlides()
      .then(setAllSlides)
      .catch((err) => toast.error(err.message));
  }, []);

  useEffect(() => {
    if (!slides.length) return;

    const loadImage = (src: string) =>
      new Promise<void>((resolve, reject) => {
        const img = new Image();
        img.src = src;
        img.onload = () => resolve();
        img.onerror = reject;
      });

    Promise.all(slides.map((s) => loadImage(s.path))).finally(() =>
      setImagesLoaded(true),
    );
  }, [slides]);

  const handleNext = () => {
    if (!slides.length) return;

    setSlideIn(false);
    setTimeout(() => {
      setActiveIndex((prev) => (prev + 1) % slides.length);
      setSlideIn(true);
    }, 300);
  };

  const handlePrev = () => {
    setSlideIn(false);
    setTimeout(() => {
      setActiveIndex((prev) => (prev - 1 + slides.length) % slides.length);
      setSlideIn(true);
    }, 300);
  };

  useEffect(() => {
    const t = setTimeout(handleNext, 5000);

    return () => clearTimeout(t);
  }, [activeIndex, slides]);

  return slides.length && imagesLoaded ? (
    <Box
      onMouseEnter={() => setHovering(true)}
      onMouseLeave={() => setHovering(false)}
      sx={{
        width: '100%',
        position: 'relative',
      }}
    >
      <Fade in={slideIn} timeout={300}>
        <Box
          key={slides[activeIndex]?.id}
          component='img'
          src={slides[activeIndex]?.path}
          sx={{
            width: '100%',
            height: 'auto',
            display: 'block',
            cursor: slides[activeIndex]?.url ? 'pointer' : undefined,
          }}
        />
      </Fade>

      <Box
        onClick={handlePrev}
        sx={{
          position: 'absolute',
          top: '50%',
          left: 0,
          transform: 'translateY(-50%)',
          zIndex: 2,
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          backgroundColor: sharedColors.gray7,
          opacity: hovering && slides.length > 1 ? 0.8 : 0,
          cursor: 'pointer',
        }}
      >
        <IconButton sx={{ color: sharedColors.white }}>
          <ChevronLeftIcon />
        </IconButton>
      </Box>

      <Box
        onClick={handleNext}
        sx={{
          position: 'absolute',
          top: '50%',
          right: 0,
          transform: 'translateY(-50%)',
          zIndex: 2,
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          backgroundColor: sharedColors.gray7,
          opacity: hovering && slides.length > 1 ? 0.8 : 0,
          cursor: 'pointer',
        }}
      >
        <IconButton sx={{ color: sharedColors.white }}>
          <ChevronRightIcon />
        </IconButton>
      </Box>
    </Box>
  ) : (
    <></>
  );
}
